import type { GuestSection } from '@whoworks/shared';
import type { ProjectRelease, ProjectReleaseInput, KnowledgeDocument, KnowledgeDocumentInput, ProjectCredential, CredentialInput, CredentialSecret } from '@whoworks/shared';
import type { SimilarTicket, TestSuggestionsResponse, TicketSummaryResponse } from '@whoworks/shared';
import type { LeadActivityInput, LeadCreateInput, LeadTemplateInput, LeadUpdateInput } from '@whoworks/shared';
import type { LeadDetail, LeadFacets, LeadImportResult, LeadListResponse, LeadTemplate } from './leads';
import type {
  ChangeMarks,
  Idea,
  IdeaComment,
  InboxItem,
  WaitingItem,
  StatusPreset,
  AppRelease,
  AssistantResponse,
  AuditEvent,
  BriefingItem,
  ChannelRead,
  ChatTyping,
  TaskBranch,
  TaskPullRequest,
  ProductWeekly,
  ChecklistItem,
  ChatMessage,
  ProjectDocument,
  UsageInfo,
  ExpenseCategory,
  ExpenseItem,
  FeedItem,
  SearchFilters,
  SearchResults,
  NoteItem,
  NoteVisibility,
  ProductDashboard,
  ProductError,
  ProductErrorLevel,
  ProductItem,
  ProductMapping,
  ProductSourceItem,
  ProductVisibility,
  SentryRegion,
  VisitsProvider,
  PlanItem,
  CommitsPage,
  GitOverview,
  ProjectCommit,
  ProjectLabel,
  ProjectListItem,
  ProjectAiOverview,
  ProjectPulse,
  PublicRunReport,
  PushPreferences,
  QuietHours,
  Sprint,
  SprintBurndown,
  TaskAttachment,
  TaskComment,
  TaskCommit,
  TaskHistoryItem,
  TaskLink,
  TaskRecurrence,
  TaskStatus,
  TaskTimeEntry,
  TaskTimePerson,
  WipLimit,
  TestComment,
  TestResultStatus,
  TestRun,
  TestRunComparison,
  TestRunStats,
  TestScenario,
  UserCommit,
  RosterUser,
  SessionItem,
  StatusMode,
  TodoTaskItem,
  UserProfile,
  WatchItem,
  WeeklyRecap,
} from './types';
import { AD_HOC_TEST_RUN_ID } from './types';
import type { AuthUser, ProjectInvitation, PublicInvitation } from './types';
import type { Role } from '@whoworks/shared';
import type { ReleaseTickets, StaleTicket, TimeExport, TimeExportFilters, WeeklyDigest } from './reports';

let csrfToken: string | null = null;

/**
 * Prihlásený je hosť (rola `guest`). API ho pustí iba na vymenované trasy a na
 * ostatné odpovie 404 (`apps/api/src/lib/guest-policy.ts`). Niekoľko dopytov
 * sa ale volá z rámu appky na každej obrazovke (zoznam ľudí, neprečítané
 * súkromné správy, sledovanie). Aby ich nemusela obchádzať každá obrazovka
 * zvlášť — a aby hosť nevidel chyby 404 — rozhoduje sa o nich tu, na jednom
 * mieste. Nastavuje to `useAuth` hneď, ako vie, kto je prihlásený.
 */
let guestMode = false;
const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') || '';

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

/**
 * Stav 0 znamená, že odpoveď neprišla vôbec — požiadavka sa nedostala von.
 * Nula preto, že žiadny skutočný HTTP stav to nie je, a appka sa na stavy pýta
 * (401 = odhlásený, 409 = konflikt v pláne), takže to nesmie kolidovať.
 */
export const NETWORK_ERROR_STATUS = 0;

const NETWORK_ERROR_MESSAGE = 'Nepodarilo sa spojiť so serverom. Skontroluj pripojenie.';

/**
 * `fetch` odmietne iba vtedy, keď sa požiadavka nedostala von: vypadnutá sieť,
 * lietadlový režim, appka bez signálu vo výťahu. Bez tohto prekladu vidí človek
 * v upozornení "Failed to fetch" — po anglicky a bez informácie, čo s tým.
 *
 * Zámerne to nie je odhlásenie: výpadok siete nie je dôvod prísť o session.
 */
export const isNetworkError = (error: unknown): boolean =>
  error instanceof ApiError && error.status === NETWORK_ERROR_STATUS;

const networkError = (): ApiError => new ApiError(NETWORK_ERROR_STATUS, NETWORK_ERROR_MESSAGE);

const buildQuery = (params: Record<string, unknown>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
};

const request = async <T>(
  path: string,
  init: RequestInit = {},
  options: { csrf?: boolean } = {},
): Promise<T> => {
  const headers = new Headers(init.headers || {});
  headers.set('Content-Type', 'application/json');

  if (options.csrf && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  const target = `${API_BASE}${path}`;

  let response: Response;
  try {
    response = await fetch(target, {
      ...init,
      headers,
      credentials: 'include',
    });
  } catch {
    throw networkError();
  }

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : null;

  if (!response.ok) {
    const message = data?.error || `Request failed (${response.status})`;
    throw new ApiError(response.status, message, data?.details);
  }

  return data as T;
};

// Raw binary upload (images go to R2 as the request body, not JSON).
const uploadImage = async (path: string, file: Blob): Promise<{ ok: boolean; url: string }> => {
  const headers = new Headers();
  headers.set('Content-Type', file.type || 'application/octet-stream');
  if (csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: file,
    });
  } catch {
    throw networkError();
  }
  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : null;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      data?.error || `Upload failed (${response.status})`,
      data?.details,
    );
  }
  return data;
};

/**
 * Požiadavka s telom, ktoré nie je JSON (import CSV, PDF príloha šablóny).
 * `request()` nastavuje Content-Type na JSON natvrdo; tu ho určuje volajúci.
 */
const requestRaw = async <T>(
  path: string,
  init: { method: string; body: BodyInit; contentType: string },
): Promise<T> => {
  const headers = new Headers();
  headers.set('Content-Type', init.contentType);
  if (csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: init.method,
      headers,
      credentials: 'include',
      body: init.body,
    });
  } catch {
    throw networkError();
  }
  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : null;
  if (!response.ok) {
    throw new ApiError(response.status, data?.error || `Request failed (${response.status})`, data?.details);
  }
  return data as T;
};

/** Adresa API pre odkaz (stiahnutie exportu, prílohy) — s rovnakým základom ako volania. */
export const apiUrl = (path: string): string => `${API_BASE}${path}`;

export const api = {
  setCsrfToken(token: string | null) {
    csrfToken = token;
  },

  setGuestMode(on: boolean) {
    guestMode = on;
  },

  isGuestMode() {
    return guestMode;
  },

  async authLogin(username: string, password: string) {
    return request<{
      user: {
        id: number;
        name: string;
        role: Role;
        createdAt: string;
        active: boolean;
        mustChangePassword: boolean;
      };
      csrfToken: string;
      sessionExpiresAt: string;
      mustChangePassword: boolean;
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  async authChangePassword(newPassword: string, currentPassword?: string) {
    return request<{ ok: boolean }>(
      '/api/auth/change_password',
      {
        method: 'POST',
        body: JSON.stringify({ newPassword, currentPassword }),
      },
      { csrf: true },
    );
  },

  async authMe() {
    return request<{
      user: {
        id: number;
        name: string;
        role: Role;
        active: boolean;
        mustChangePassword?: boolean;
      };
      csrfToken: string;
      pushConfigured: boolean;
      streakDays: number;
      generalUnread: number;
    }>('/api/auth/me');
  },

  async appReleaseCurrent() {
    return request<{ release: AppRelease; history: AppRelease[] }>('/api/app-release/current');
  },

  /** Kde všade je človek prihlásený. Bez názvu zariadenia — ten sa neukladá. */
  async authSessions() {
    return request<{
      items: Array<{
        id: number;
        createdAt: string;
        lastSeenAt: string | null;
        expiresAt: string;
        current: boolean;
      }>;
    }>('/api/auth/sessions');
  },

  async authRevokeOtherSessions() {
    return request<{ ok: boolean; revoked: number }>(
      '/api/auth/sessions/revoke_others',
      { method: 'POST' },
      { csrf: true },
    );
  },

  async authLogout() {
    return request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }, { csrf: true });
  },

  // Verejný výber: kto sa smie prihlásiť. Účet bez hesla sa v ňom neobjaví.
  async authUsers() {
    return request<{
      users: Array<{ id: number; name: string; avatarUrl: string | null }>;
    }>('/api/auth/users');
  },

  async usersList() {
    if (guestMode) {
      // Hosť nevidí zoznam tímu, iba ľudí zo svojich projektov (meno a fotka).
      // Tvar sa doplní na RosterUser, aby doska, chat a komentáre nemuseli
      // rozlišovať, odkiaľ mená prišli. Ostatné polia sú prázdne, nie vymyslené.
      const shared = await request<{ items: Array<{ id: number; name: string; avatarUrl: string | null }> }>(
        '/api/users/shared',
      );
      return {
        items: shared.items.map(
          (person): RosterUser => ({
            id: person.id,
            name: person.name,
            role: 'member',
            active: true,
            bio: null,
            statusText: null,
            statusMode: 'available',
            statusColor: null,
            avatarUrl: person.avatarUrl,
            coverUrl: null,
            lastSeenAt: null,
            online: false,
            weekSeconds: 0,
            streakDays: 0,
          }),
        ),
      };
    }
    return request<{ items: RosterUser[] }>('/api/users/list');
  },

  async changes() {
    return request<ChangeMarks>('/api/changes');
  },

  async updateProfile(payload: {
    bio?: string | null;
    statusText?: string | null;
    statusMode?: 'available' | 'busy' | 'away' | 'focus' | 'offline';
    statusColor?: string | null;
    githubLogin?: string | null;
  }) {
    return request<{ ok: boolean }>(
      '/api/users/me/profile',
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  /**
   * Zaradí projekt pod iný (skupina), alebo ho zo skupiny vyberie (null).
   * Nič sa nepresúva — tikety si nechajú svoje čísla.
   */
  async projectSetParent(id: number, parentProjectId: number | null) {
    return request<{ ok: boolean }>(
      `/api/projects/${id}/parent`,
      { method: 'PATCH', body: JSON.stringify({ parentProjectId }) },
      { csrf: true },
    );
  },

  async projectCommits(id: number, limit = 15) {
    return request<{ items: ProjectCommit[] }>(
      `/api/projects/${id}/commits${buildQuery({ limit })}`,
    );
  },
  async projectGitOverview(id: number) {
    return request<GitOverview>(`/api/projects/${id}/git`);
  },
  async projectCommitsFeed(
    id: number,
    opts: { branch?: string | null; cursorTime?: string; cursorId?: number; limit?: number } = {},
  ) {
    return request<CommitsPage>(
      `/api/projects/${id}/commits${buildQuery({
        branch: opts.branch ?? undefined,
        cursorTime: opts.cursorTime,
        cursorId: opts.cursorId,
        limit: opts.limit ?? 30,
      })}`,
    );
  },
  async projectRepoBackfill(id: number) {
    return request<{
      ok: boolean;
      imported: number;
      fetched: number;
      branches: number;
      rateLimited: boolean;
    }>(
      `/api/projects/${id}/repo/backfill`,
      { method: 'POST', body: JSON.stringify({}) },
      { csrf: true },
    );
  },
  async userCommits(id: number, limit = 15) {
    return request<{ items: UserCommit[] }>(`/api/users/${id}/commits${buildQuery({ limit })}`);
  },

  async presenceHeartbeat(
    payload: {
      statusMode?: 'available' | 'busy' | 'away' | 'focus' | 'offline';
      statusText?: string | null;
    } = {},
  ) {
    return request<{ ok: boolean }>(
      '/api/presence/heartbeat',
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async uploadAvatar(file: Blob) {
    return uploadImage('/api/users/me/avatar', file);
  },

  async uploadCover(file: Blob) {
    return uploadImage('/api/users/me/cover', file);
  },

  async projectPermissions(projectId: number) {
    return request<{ permissions: GuestSection[] }>(`/api/projects/${projectId}/permissions`);
  },
  async guestPermissions(projectId: number, userId: number) {
    return request<{ permissions: GuestSection[] }>(`/api/projects/${projectId}/members/${userId}/permissions`);
  },
  async setGuestPermissions(projectId: number, userId: number, permissions: GuestSection[]) {
    return request<{ permissions: GuestSection[] }>(`/api/projects/${projectId}/members/${userId}/permissions`, {
      method: 'PUT', body: JSON.stringify(permissions),
    }, { csrf: true });
  },
  async projectMembers(projectId: number) {
    return request<{
      members: Array<{
        userId: number;
        role: string;
        name: string;
        /** Človek je hosť (rola `guest`) — pre správu členov. */
        guest?: boolean;
        avatarUrl: string | null;
        statusMode: StatusMode;
        statusColor: string | null;
        online: boolean;
      }>;
    }>(`/api/projects/${projectId}/members`);
  },

  // ---- Pozvánky hostí -------------------------------------------------------
  async projectInvitations(projectId: number) {
    return request<{ enabled: boolean; message?: string; items: ProjectInvitation[] }>(
      `/api/projects/${projectId}/invitations`,
    );
  },

  async projectInvitationCreate(
    projectId: number,
    payload: { name?: string; email?: string; expiresInDays: 1 | 7 | 30 },
  ) {
    return request<{ ok: boolean; url: string; path: string; projectName: string; invitation: ProjectInvitation }>(
      `/api/projects/${projectId}/invitations`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async projectInvitationRevoke(projectId: number, invitationId: number) {
    return request<{ ok: boolean }>(
      `/api/projects/${projectId}/invitations/${invitationId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  /** Verejné: čo stojí v pozvánke. Neplatná pozvánka je vždy 404. */
  async invitationPublic(token: string) {
    return request<PublicInvitation>(`/api/invitations/${encodeURIComponent(token)}`);
  },

  async invitationAccept(token: string, payload: { name: string; password: string }) {
    return request<{
      ok: boolean;
      user: AuthUser & { createdAt: string };
      csrfToken: string;
      projectId: number;
    }>(`/api/invitations/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async projectAddMember(projectId: number, userId: number, role: 'owner' | 'member' = 'member') {
    return request<{ ok: boolean }>(
      `/api/projects/${projectId}/members`,
      { method: 'POST', body: JSON.stringify({ userId, role }) },
      { csrf: true },
    );
  },

  async projectRemoveMember(projectId: number, userId: number) {
    return request<{ ok: boolean }>(
      `/api/projects/${projectId}/members/${userId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  async usersProfile(userId: number) {
    return request<UserProfile>(`/api/users/${userId}/profile`);
  },
  async recapWeekly(refresh = false) {
    return request<WeeklyRecap>(`/api/recap/weekly${refresh ? '?refresh=1' : ''}`);
  },
  async assistantMessage(payload: {
    message: string;
    history?: Array<{ role: 'user' | 'assistant'; content: string }>;
    context?: {
      path: string;
      timeZone?: string;
      localDayStart?: string;
      localDayEnd?: string;
    };
  }) {
    return request<AssistantResponse>(
      '/api/assistant/message',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },
  // AI nad ticketom (apps/api/src/ticket-ai.ts). POST kvôli CSRF a limitu —
  // každé volanie stojí volanie modelu.
  async ticketAiSummary(taskId: number) {
    return request<TicketSummaryResponse>(
      `/api/ticket-ai/${taskId}/summary`,
      { method: 'POST', body: '{}' },
      { csrf: true },
    );
  },
  async ticketAiTestSuggestions(taskId: number) {
    return request<TestSuggestionsResponse>(
      `/api/ticket-ai/${taskId}/test-suggestions`,
      { method: 'POST', body: '{}' },
      { csrf: true },
    );
  },
  async ticketsSimilar(params: { projectId: number; q: string; excludeId?: number }) {
    return request<{ items: SimilarTicket[] }>(`/api/ticket-ai/similar${buildQuery(params)}`);
  },
  async projectAiOverview(projectId: number, refresh = false) {
    return request<ProjectAiOverview>(
      `/api/projects/${projectId}/ai-overview${refresh ? '?refresh=1' : ''}`,
    );
  },
  async usersPing(userId: number, message?: string) {
    return request<{ ok: boolean; delivered: number }>(
      `/api/users/${userId}/ping`,
      { method: 'POST', body: JSON.stringify({ message }) },
      { csrf: true },
    );
  },
  async projectsPulse(projectId: number) {
    return request<ProjectPulse>(`/api/projects/${projectId}/pulse`);
  },
  async eventsList(
    filters: { projectId?: number; types?: string; before?: number; limit?: number } = {},
  ) {
    return request<{ items: FeedItem[]; nextBefore: number | null }>(
      `/api/events/list${buildQuery(filters)}`,
    );
  },
  async eventsAudit(filters: { limit?: number; before?: number } = {}) {
    return request<{ items: AuditEvent[]; nextBefore: number | null }>(
      `/api/events/audit${buildQuery(filters)}`,
    );
  },
  async search(q: string, filters: SearchFilters = {}) {
    return request<SearchResults>(`/api/search${buildQuery({ q, ...filters })}`);
  },
  async watchesCreate(payload: { kind: 'project' | 'user'; targetId: number }) {
    return request<{ ok: boolean }>(
      '/api/watches/create',
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async watchesList() {
    // Sledovanie dostupnosti ľudí je vec tímu; hosťovi sa zoznam neposiela.
    if (guestMode) return { items: [] as WatchItem[] };
    return request<{ items: WatchItem[] }>('/api/watches/list');
  },
  async watchesDelete(id: number) {
    return request<{ ok: boolean }>(`/api/watches/${id}`, { method: 'DELETE' }, { csrf: true });
  },
  async messagesList(projectId: number, filters: { before?: number; limit?: number } = {}) {
    return request<{
      items: ChatMessage[];
      nextBefore: number | null;
      reads: ChannelRead[];
      typing?: ChatTyping[];
    }>(
      `/api/projects/${projectId}/messages${buildQuery(filters)}`,
    );
  },
  async messagesCreate(
    projectId: number,
    body: string,
    replyToId?: number,
    attachment?: { key: string; name: string; mime: string; size: number },
    poll?: { question: string; allowMultiple: boolean; options: string[] },
  ) {
    return request<{ ok: boolean; message: ChatMessage }>(
      `/api/projects/${projectId}/messages`,
      { method: 'POST', body: JSON.stringify({ body, replyToId, attachment, poll }) },
      { csrf: true },
    );
  },
  // Raw-body chat file upload (<= 5 MB, images). Returns the R2 key echoed
  // back when sending the message.
  async uploadChatFile(file: Blob, name: string) {
    const headers = new Headers();
    headers.set('Content-Type', file.type || 'application/octet-stream');
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
    const response = await fetch(`${API_BASE}/api/uploads/chat?name=${encodeURIComponent(name)}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: file,
    });
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await response.json() : null;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.error || `Upload zlyhal (${response.status})`,
        data?.details,
      );
    }
    return data as { ok: boolean; key: string; name: string; mime: string; size: number };
  },
  async messagesRead(projectId: number, lastReadMessageId: number) {
    return request<{ ok: boolean }>(
      `/api/projects/${projectId}/messages/read`,
      { method: 'POST', body: JSON.stringify({ lastReadMessageId }) },
      { csrf: true },
    );
  },
  async messagesUpdate(id: number, body: string) {
    return request<{ ok: boolean; editedAt: string }>(
      `/api/messages/${id}`,
      { method: 'PATCH', body: JSON.stringify({ body }) },
      { csrf: true },
    );
  },
  /**
   * Signál „píšem" / „prestal som". Klient ho posiela najviac raz za pár
   * sekúnd; server si ho drží len krátko (chat-typing.ts).
   */
  async chatTyping(
    channel: { kind: 'general' } | { kind: 'project'; projectId: number } | { kind: 'dm'; userId: number },
    typing: boolean,
  ) {
    const body =
      channel.kind === 'general'
        ? { channel: 'general', typing }
        : channel.kind === 'project'
          ? { channel: 'project', id: channel.projectId, typing }
          : { channel: 'dm', id: channel.userId, typing };
    return request<{ ok: boolean }>(
      '/api/chat/typing',
      { method: 'POST', body: JSON.stringify(body) },
      { csrf: true },
    );
  },
  async messagesDelete(id: number) {
    return request<{ ok: boolean }>(`/api/messages/${id}`, { method: 'DELETE' }, { csrf: true });
  },
  async messageReactionToggle(id: number, emoji: string) {
    return request<{ ok: boolean; added: boolean }>(
      `/api/messages/${id}/reactions`,
      { method: 'POST', body: JSON.stringify({ emoji }) },
      { csrf: true },
    );
  },
  async pollVote(pollId: number, optionId: number) {
    return request<{ ok: boolean; voted: boolean }>(
      `/api/polls/${pollId}/vote`,
      { method: 'POST', body: JSON.stringify({ optionId }) },
      { csrf: true },
    );
  },
  async generalMessagesList(filters: { before?: number; limit?: number } = {}) {
    return request<{
      items: ChatMessage[];
      nextBefore: number | null;
      reads: ChannelRead[];
      typing?: ChatTyping[];
    }>(
      `/api/chat/general${buildQuery(filters)}`,
    );
  },
  async generalMessagesCreate(
    body: string,
    replyToId?: number,
    attachment?: { key: string; name: string; mime: string; size: number },
    poll?: { question: string; allowMultiple: boolean; options: string[] },
  ) {
    return request<{ ok: boolean; message: ChatMessage }>(
      '/api/chat/general',
      { method: 'POST', body: JSON.stringify({ body, replyToId, attachment, poll }) },
      { csrf: true },
    );
  },
  async generalMessagesRead(lastReadMessageId: number) {
    return request<{ ok: boolean }>(
      '/api/chat/general/read',
      { method: 'POST', body: JSON.stringify({ lastReadMessageId }) },
      { csrf: true },
    );
  },
  async dmMessagesList(userId: number, filters: { before?: number; limit?: number } = {}) {
    return request<{
      items: ChatMessage[];
      nextBefore: number | null;
      reads: ChannelRead[];
      typing?: ChatTyping[];
    }>(
      `/api/chat/dm/${userId}${buildQuery(filters)}`,
    );
  },
  async dmMessagesCreate(
    userId: number,
    body: string,
    replyToId?: number,
    attachment?: { key: string; name: string; mime: string; size: number },
    poll?: { question: string; allowMultiple: boolean; options: string[] },
  ) {
    return request<{ ok: boolean; message: ChatMessage }>(
      `/api/chat/dm/${userId}`,
      { method: 'POST', body: JSON.stringify({ body, replyToId, attachment, poll }) },
      { csrf: true },
    );
  },
  async projectDocumentsList(projectId: number) {
    return request<{ items: ProjectDocument[] }>(`/api/projects/${projectId}/documents`);
  },
  // Raw-body document upload (<= 5 MB).
  async projectDocumentUpload(projectId: number, file: Blob, name: string) {
    const headers = new Headers();
    headers.set('Content-Type', file.type || 'application/octet-stream');
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
    const response = await fetch(
      `${API_BASE}/api/projects/${projectId}/documents?name=${encodeURIComponent(name)}`,
      { method: 'POST', headers, credentials: 'include', body: file },
    );
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await response.json() : null;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.error || `Upload zlyhal (${response.status})`,
        data?.details,
      );
    }
    return data as { ok: boolean; document: ProjectDocument };
  },
  async projectDocumentDelete(projectId: number, documentId: number) {
    return request<{ ok: boolean }>(
      `/api/projects/${projectId}/documents/${documentId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  // ---- Ticket labels -------------------------------------------------------
  async projectLabelsList(projectId: number) {
    return request<{ items: ProjectLabel[] }>(`/api/projects/${projectId}/labels`);
  },
  async projectLabelCreate(projectId: number, payload: { name: string; color?: string }) {
    return request<{ label: ProjectLabel }>(
      `/api/projects/${projectId}/labels`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async projectLabelDelete(projectId: number, labelId: number) {
    return request<{ ok: boolean }>(
      `/api/projects/${projectId}/labels/${labelId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },
  async projectLabelUpdate(
    projectId: number,
    labelId: number,
    payload: { name?: string; color?: string },
  ) {
    return request<{ label: ProjectLabel }>(
      `/api/projects/${projectId}/labels/${labelId}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  // ---- Testing section -----------------------------------------------------
  // runId scopes every result to one test run; 0 (default) = the ad-hoc bucket.
  async projectTestsList(projectId: number, runId = AD_HOC_TEST_RUN_ID) {
    return request<{ items: TestScenario[] }>(
      `/api/projects/${projectId}/tests${runId ? `?runId=${runId}` : ''}`,
    );
  },
  async projectTestRunsList(projectId: number) {
    return request<{ items: TestRun[]; adHoc: TestRunStats }>(
      `/api/projects/${projectId}/test-runs`,
    );
  },
  // base = starší beh, head = novší; 0 je „Bez behu“.
  async projectTestRunsCompare(projectId: number, baseRunId: number, headRunId: number) {
    return request<TestRunComparison>(
      `/api/projects/${projectId}/test-runs/compare?base=${baseRunId}&head=${headRunId}`,
    );
  },
  async projectTestRunCreate(projectId: number, payload: { name: string }) {
    return request<{ ok: boolean; id: number | null }>(
      `/api/projects/${projectId}/test-runs`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async testRunUpdate(runId: number, payload: { name?: string; closed?: boolean }) {
    return request<{ ok: boolean }>(
      `/api/test-runs/${runId}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async testRunShare(runId: number) {
    return request<{ ok: boolean; token: string; path: string }>(
      `/api/test-runs/${runId}/share`,
      { method: 'POST', body: JSON.stringify({}) },
      { csrf: true },
    );
  },
  async testRunUnshare(runId: number) {
    return request<{ ok: boolean }>(
      `/api/test-runs/${runId}/share`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },
  // Public report — no session, no CSRF.
  async publicRunReport(token: string) {
    return request<PublicRunReport>(`/api/public/test-runs/${encodeURIComponent(token)}`);
  },
  async testRunDelete(runId: number) {
    return request<{ ok: boolean }>(
      `/api/test-runs/${runId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },
  async projectTestsBulk(
    projectId: number,
    payload: {
      ids: number[];
      action: 'delete' | 'archive' | 'unarchive' | 'pass' | 'fail';
      runId?: number;
    },
  ) {
    // skippedIds: scenáre, ktoré „funguje“ nedostali, lebo držia otvorený ticket.
    return request<{ ok: boolean; affected: number; skipped: number; skippedIds?: number[] }>(
      `/api/projects/${projectId}/tests/bulk`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async projectTestCreate(
    projectId: number,
    payload: { title: string; steps: string[]; section?: string | null },
  ) {
    return request<{ ok: boolean; id: number }>(
      `/api/projects/${projectId}/tests`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async projectTestsImport(
    projectId: number,
    payload: {
      scenarios: Array<{
        title: string;
        steps: string[];
        priority?: 'P1' | 'P2' | 'P3' | null;
        section?: string | null;
      }>;
    },
  ) {
    return request<{ ok: boolean; imported: number; skipped: number }>(
      `/api/projects/${projectId}/tests/import`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async testUpdate(
    scenarioId: number,
    payload: {
      title?: string;
      steps?: string[];
      assigneeUserId?: number | null;
      priority?: 'P1' | 'P2' | 'P3' | null;
      section?: string | null;
      archived?: boolean;
    },
  ) {
    return request<{ ok: boolean }>(
      `/api/tests/${scenarioId}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async testCommentsList(scenarioId: number) {
    return request<{ items: TestComment[] }>(`/api/tests/${scenarioId}/comments`);
  },
  // Upload-first: store the file, then create the comment referencing it.
  async testCommentUpload(scenarioId: number, file: Blob, name: string) {
    const headers = new Headers();
    headers.set('Content-Type', file.type || 'application/octet-stream');
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
    const response = await fetch(
      `${API_BASE}/api/tests/${scenarioId}/comment_upload?name=${encodeURIComponent(name)}`,
      { method: 'POST', headers, credentials: 'include', body: file },
    );
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await response.json() : null;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.error || `Upload zlyhal (${response.status})`,
        data?.details,
      );
    }
    return data as {
      ok: boolean;
      attachment: { key: string; name: string; mime: string; size: number };
    };
  },
  async testCommentCreate(
    scenarioId: number,
    payload: {
      body?: string;
      attachments?: Array<{ key: string; name: string; mime: string; size: number }>;
    },
  ) {
    return request<{ ok: boolean; id: number }>(
      `/api/tests/${scenarioId}/comments`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async testCommentDelete(scenarioId: number, commentId: number) {
    return request<{ ok: boolean }>(
      `/api/tests/${scenarioId}/comments/${commentId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },
  testCommentAttachmentUrl(scenarioId: number, commentId: number, attachmentId?: number) {
    // Bez čísla ide o starú cestu, ktorá vracia prvú prílohu.
    return attachmentId === undefined
      ? `${API_BASE}/api/tests/${scenarioId}/comments/${commentId}/attachment`
      : `${API_BASE}/api/tests/${scenarioId}/comments/${commentId}/attachments/${attachmentId}`;
  },
  async testDelete(scenarioId: number) {
    return request<{ ok: boolean }>(
      `/api/tests/${scenarioId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },
  // Copies what defines the scenario and none of what recorded it.
  async testDuplicate(scenarioId: number) {
    return request<{ ok: boolean; id: number | null }>(
      `/api/tests/${scenarioId}/duplicate`,
      { method: 'POST' },
      { csrf: true },
    );
  },
  async testResultSet(
    scenarioId: number,
    payload: {
      status: TestResultStatus;
      note?: string;
      runId?: number;
      /** Only for 'blocked': the scenario that has to be fixed first. */
      blockedByScenarioId?: number | null;
      /** Bug flow on 'fail': open a ticket, adopt one, or record it bare. */
      bugTicket?: 'create' | 'link' | 'none';
      bugTicketId?: number;
    },
  ) {
    return request<{ ok: boolean; ticket: { id: number; ticketNumber: number | null } | null }>(
      `/api/tests/${scenarioId}/result`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async testResultClear(scenarioId: number, runId = AD_HOC_TEST_RUN_ID) {
    return request<{ ok: boolean }>(
      `/api/tests/${scenarioId}/result${runId ? `?runId=${runId}` : ''}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  // ---- Ticket commits ------------------------------------------------------
  // Commits linked by mentioning the ticket key (T-123) in their message.
  async taskCommitsList(taskId: number) {
    return request<{ items: TaskCommit[] }>(`/api/tasks/${taskId}/commits`);
  },
  // PR a vetvy, ktoré tiketu spomínajú v názve (webhook GitHubu).
  async taskPullRequestsList(taskId: number) {
    return request<{ pullRequests: TaskPullRequest[]; branches: TaskBranch[] }>(
      `/api/tasks/${taskId}/pull-requests`,
    );
  },

  // ---- Ticket dependencies -------------------------------------------------
  async taskLinksList(taskId: number) {
    return request<{ blocks: TaskLink[]; blockedBy: TaskLink[] }>(`/api/tasks/${taskId}/links`);
  },
  async taskLinkAdd(
    taskId: number,
    payload: { taskId: number; direction: 'blocks' | 'blockedBy' },
  ) {
    return request<{ ok: boolean }>(
      `/api/tasks/${taskId}/links`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async taskLinkRemove(taskId: number, otherId: number) {
    return request<{ ok: boolean }>(
      `/api/tasks/${taskId}/links/${otherId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  // ---- Ticket checklist ----------------------------------------------------
  async taskChecklistList(taskId: number) {
    return request<{ items: ChecklistItem[] }>(`/api/tasks/${taskId}/checklist`);
  },
  async taskChecklistAdd(taskId: number, payload: { title: string }) {
    return request<{ ok: boolean; id: number }>(
      `/api/tasks/${taskId}/checklist`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async checklistUpdate(itemId: number, payload: { title?: string; done?: boolean }) {
    return request<{ ok: boolean }>(
      `/api/checklist/${itemId}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async checklistDelete(itemId: number) {
    return request<{ ok: boolean }>(
      `/api/checklist/${itemId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  // ---- Ticket time ---------------------------------------------------------
  // Work sessions booked onto this ticket, plus their total.
  async taskTimeList(taskId: number) {
    return request<{
      items: TaskTimeEntry[];
      totalSeconds: number;
      /** Súčet po ľuďoch zo všetkých sedení; staršie API ho neposiela. */
      byUser?: TaskTimePerson[];
    }>(`/api/tasks/${taskId}/time`);
  },

  // ---- Ticket attachments --------------------------------------------------
  async taskAttachmentsList(taskId: number) {
    return request<{ items: TaskAttachment[] }>(`/api/tasks/${taskId}/attachments`);
  },
  taskAttachmentDownloadUrl(taskId: number, attachmentId: number) {
    return `${API_BASE}/api/tasks/${taskId}/attachments/${attachmentId}/download`;
  },
  // Raw-body upload (<= 5 MB). Photos are resized client-side before calling this.
  async taskAttachmentUpload(taskId: number, file: Blob, name: string) {
    const headers = new Headers();
    headers.set('Content-Type', file.type || 'application/octet-stream');
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
    const response = await fetch(
      `${API_BASE}/api/tasks/${taskId}/attachments?name=${encodeURIComponent(name)}`,
      { method: 'POST', headers, credentials: 'include', body: file },
    );
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await response.json() : null;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.error || `Upload zlyhal (${response.status})`,
        data?.details,
      );
    }
    return data as { ok: boolean; attachment: TaskAttachment };
  },
  async taskAttachmentDelete(taskId: number, attachmentId: number) {
    return request<{ ok: boolean }>(
      `/api/tasks/${taskId}/attachments/${attachmentId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  // ---- Ticket comments (JIRA-style updates) --------------------------------
  async taskCommentsList(taskId: number) {
    return request<{ items: TaskComment[] }>(`/api/tasks/${taskId}/comments`);
  },
  async taskCommentUpload(taskId: number, file: Blob, name: string) {
    const headers = new Headers();
    headers.set('Content-Type', file.type || 'application/octet-stream');
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
    const response = await fetch(
      `${API_BASE}/api/tasks/${taskId}/comment_upload?name=${encodeURIComponent(name)}`,
      { method: 'POST', headers, credentials: 'include', body: file },
    );
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await response.json() : null;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.error || `Upload zlyhal (${response.status})`,
        data?.details,
      );
    }
    return data as {
      ok: boolean;
      attachment: { key: string; name: string; mime: string; size: number };
    };
  },
  async taskCommentCreate(
    taskId: number,
    payload: {
      body?: string;
      attachments?: Array<{ key: string; name: string; mime: string; size: number }>;
    },
  ) {
    return request<{ ok: boolean; id: number }>(
      `/api/tasks/${taskId}/comments`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async taskCommentDelete(taskId: number, commentId: number) {
    return request<{ ok: boolean }>(
      `/api/tasks/${taskId}/comments/${commentId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },
  taskCommentAttachmentUrl(taskId: number, commentId: number, attachmentId?: number) {
    // Bez čísla prílohy ide o starú cestu, ktorá vracia prvú. Používa sa už len
    // pre komentáre, ktoré prišli zo staršej appky bez zoznamu príloh.
    return attachmentId === undefined
      ? `${API_BASE}/api/tasks/${taskId}/comments/${commentId}/attachment`
      : `${API_BASE}/api/tasks/${taskId}/comments/${commentId}/attachments/${attachmentId}`;
  },
  // ---- Osobná schránka -----------------------------------------------------
  async inboxList(unreadOnly = false) {
    return request<{ items: InboxItem[]; unread: number }>(
      `/api/inbox${unreadOnly ? '?unread=1' : ''}`,
    );
  },
  async inboxMarkRead(ids?: number[]) {
    // Bez zoznamu = „mám prečítané“ na celú schránku.
    return request<{ ok: boolean; unread: number }>(
      '/api/inbox/read',
      { method: 'POST', body: JSON.stringify(ids ? { ids } : {}) },
      { csrf: true },
    );
  },
  async inboxWaiting() {
    return request<{ items: WaitingItem[] }>('/api/inbox/waiting');
  },
  // Prehľady (apps/api/src/reports.ts) — iba čítanie.
  async staleTickets(projectId: number) {
    // Prehľady (reports) sú pre tím. Odznak „zaseknuté" na doske sa hosťovi
    // jednoducho neukáže, namiesto chyby 404 pri každej karte.
    if (guestMode) return { items: [] as StaleTicket[] };
    return request<{ items: StaleTicket[] }>(`/api/reports/stale${buildQuery({ projectId })}`);
  },
  async weeklyDigest(week?: string) {
    return request<WeeklyDigest>(`/api/reports/digest${buildQuery({ week })}`);
  },
  async releaseDoneTickets(projectId: number) {
    return request<ReleaseTickets>(`/api/reports/release-tickets${buildQuery({ projectId })}`);
  },
  async timeExport(filters: TimeExportFilters) {
    return request<TimeExport>(`/api/reports/time${buildQuery(filters)}`);
  },
  // `synced: false` = server ešte nemá tabuľku (migrácia nebežala); appka
  // vtedy ostane pri localStorage. Pozri apps/api/src/waiting.ts.
  async inboxWaitingSeen() {
    return request<{ synced: boolean; seenUntil: string | null }>('/api/inbox/waiting/seen');
  },
  async inboxWaitingSeenUpdate(seenUntil: string) {
    return request<{ synced: boolean; seenUntil: string | null }>(
      '/api/inbox/waiting/seen',
      { method: 'PUT', body: JSON.stringify({ seenUntil }) },
      { csrf: true },
    );
  },

  async adminUsage() {
    return request<UsageInfo>('/api/admin/usage');
  },
  async dmMessagesRead(userId: number, lastReadMessageId: number) {
    return request<{ ok: boolean }>(
      `/api/chat/dm/${userId}/read`,
      { method: 'POST', body: JSON.stringify({ lastReadMessageId }) },
      { csrf: true },
    );
  },
  async dmUnread() {
    // Hosť súkromné správy nemá; rám appky sa na ne pýta každú minútu.
    if (guestMode) return { items: [] as Array<{ peerId: number; unread: number }> };
    return request<{ items: Array<{ peerId: number; unread: number }> }>('/api/chat/dm/unread');
  },
  async sessionsBriefingRead(sessionId: number) {
    return request<{ ok: boolean }>(
      '/api/sessions/briefing_read',
      { method: 'POST', body: JSON.stringify({ sessionId }) },
      { csrf: true },
    );
  },

  async knowledgeDocuments(projectId: number, archived = false) {
    return request<{ items: Omit<KnowledgeDocument, 'content'>[] }>(`/api/projects/${projectId}/knowledge/documents${archived ? "?archived=1" : ""}`);
  },
  async knowledgeDocument(projectId: number, id: string) {
    return request<{ document: KnowledgeDocument }>(`/api/projects/${projectId}/knowledge/documents/${id}`);
  },
  async knowledgeSaveDocument(projectId: number, body: KnowledgeDocumentInput, id?: string) {
    return request<{ id?: string; ok?: boolean }>(`/api/projects/${projectId}/knowledge/documents${id ? '/' + id : ''}`, { method: id ? 'PATCH' : 'POST', body: JSON.stringify(body) }, { csrf: true });
  },
  async knowledgeArchive(projectId: number, kind: 'documents' | 'credentials', id: string, version: number) {
    return request<{ ok: boolean }>(`/api/projects/${projectId}/knowledge/${kind}/${id}/archive`, { method: 'POST', body: JSON.stringify({ version }) }, { csrf: true });
  },
  async knowledgeRestore(projectId: number, id: string, version: number) {
    return request<{ok: boolean}>(`/api/projects/${projectId}/knowledge/documents/${id}/restore`, {method: 'POST', body: JSON.stringify({version})}, {csrf: true});
  },
  async knowledgeHistory(projectId: number, id: string) {
    return request<{items: Array<KnowledgeDocumentInput & {version: number; updatedAt: string; updatedBy: string}>}>(`/api/projects/${projectId}/knowledge/documents/${id}/history`);
  },
  async knowledgeLinks(projectId: number, id: string, body: {version: number; scenarioId: number | null; credentialId: string | null}) {
    return request<{ok: boolean}>(`/api/projects/${projectId}/knowledge/documents/${id}/links`, {method: 'PATCH', body: JSON.stringify(body)}, {csrf: true});
  },
  async projectReleases(projectId: number) {
    return request<{items: ProjectRelease[]}>(`/api/projects/${projectId}/knowledge/releases`);
  },
  async projectReleaseSave(projectId: number, body: ProjectReleaseInput, id?: string) {
    return request<{ok?: boolean; id?: string}>(`/api/projects/${projectId}/knowledge/releases${id ? '/' + id : ''}`, {method: id ? 'PATCH' : 'POST', body: JSON.stringify(body)}, {csrf: true});
  },
  async projectReleaseVerify(projectId: number, id: string, version: number) {
    return request<{ok: boolean}>(`/api/projects/${projectId}/knowledge/releases/${id}/verify`, {method: 'POST', body: JSON.stringify({version})}, {csrf: true});
  },
  async knowledgeCredentials(projectId: number) {
    return request<{ items: ProjectCredential[] }>(`/api/projects/${projectId}/knowledge/credentials`);
  },
  async knowledgeRevealCredential(projectId: number, id: string) {
    return request<CredentialSecret>(`/api/projects/${projectId}/knowledge/credentials/${id}/reveal`, { method: 'POST' }, { csrf: true });
  },
  async knowledgeSaveCredential(projectId: number, body: CredentialInput, id?: string) {
    return request<{ id?: string; ok?: boolean }>(`/api/projects/${projectId}/knowledge/credentials${id ? '/' + id : ''}`, { method: id ? 'PATCH' : 'POST', body: JSON.stringify(body) }, { csrf: true });
  },

  async usersCreate(payload: {
    name: string;
    role: 'owner' | 'admin' | 'member';
    initialPassword?: string;
  }) {
    return request<{ ok: boolean; id: number; initialPassword: string }>(
      '/api/users/create',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async usersResetPassword(id: number) {
    return request<{ ok: boolean; initialPassword: string }>(
      `/api/users/${id}/reset-password`,
      { method: 'POST' },
      { csrf: true },
    );
  },

  async usersDeactivate(id: number, active: boolean) {
    return request<{ ok: boolean }>(
      `/api/users/${id}/deactivate`,
      {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      },
      { csrf: true },
    );
  },

  async usersChangeRole(id: number, role: Role) {
    return request<{ ok: boolean }>(
      `/api/users/${id}/role`,
      {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      },
      { csrf: true },
    );
  },

  async usersDelete(id: number) {
    return request<{ ok: boolean }>(`/api/users/${id}`, { method: 'DELETE' }, { csrf: true });
  },

  async projectsList(includeArchived = false) {
    const data = await request<{ items: ProjectListItem[] }>(
      `/api/projects/list${buildQuery({ includeArchived: includeArchived ? 1 : 0 })}`,
    );

    localStorage.setItem('whoworks:lastProjects', JSON.stringify(data.items));
    return data;
  },

  async projectsCreate(payload: {
    name: string;
    description?: string | null;
    color: string;
    memberUserIds?: number[];
  }) {
    return request<{ ok: boolean; id: number }>(
      '/api/projects/create',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async projectsUpdate(
    id: number,
    payload: { name?: string; description?: string | null; color?: string },
  ) {
    return request<{ ok: boolean }>(
      `/api/projects/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async projectRepoGet(id: number) {
    return request<{
      repoFullName: string | null;
      webhookSecret: string | null;
      webhookUrl: string;
      hasToken: boolean;
    }>(`/api/projects/${id}/repo`);
  },
  async projectRepoSet(id: number, repoFullName: string | null) {
    return request<{ ok: boolean; repoFullName: string | null; webhookSecret: string | null }>(
      `/api/projects/${id}/repo`,
      { method: 'PATCH', body: JSON.stringify({ repoFullName }) },
      { csrf: true },
    );
  },
  async projectRepoTokenSet(id: number, token: string | null) {
    return request<{ ok: boolean; hasToken: boolean }>(
      `/api/projects/${id}/repo/token`,
      { method: 'PATCH', body: JSON.stringify({ token }) },
      { csrf: true },
    );
  },

  async projectsArchive(id: number, archived: boolean) {
    return request<{ ok: boolean }>(
      `/api/projects/${id}/archive`,
      {
        method: 'PATCH',
        body: JSON.stringify({ archived }),
      },
      { csrf: true },
    );
  },

  async sessionsStart(payload: {
    projectId: number;
    tag?: string;
    startedAt?: string;
    // Optional ticket the tracked time is booked onto.
    taskId?: number | null;
  }) {
    return request<{
      ok: boolean;
      session: {
        id: number;
        projectId: number;
        userId: number;
        startedAt: string;
      };
      briefing: BriefingItem | null;
    }>(
      '/api/sessions/start',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async sessionsStop(payload: {
    sessionId: number;
    endNote: string;
    handoffToUserId?: number | null;
    tag?: string;
    tags?: string[];
    taskId?: number | null;
    briefing?: string;
    endedAt?: string;
  }) {
    return request<{ ok: boolean; session: SessionItem }>(
      '/api/sessions/stop',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  // Work that was never timed, written straight in as a finished session.
  async sessionsManual(payload: {
    projectId: number;
    taskId?: number | null;
    startedAt: string;
    endedAt: string;
    endNote: string;
    tags?: string[];
  }) {
    return request<{ ok: boolean; session: SessionItem }>(
      '/api/sessions/manual',
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async sessionsList(filters: {
    from?: string;
    to?: string;
    projectId?: number;
    userId?: number;
    limit?: number;
  }) {
    return request<{ items: SessionItem[] }>(`/api/sessions/list${buildQuery(filters)}`);
  },

  async sessionsUpdateLog(
    sessionId: number,
    payload: {
      endNote: string;
      tag?: string;
      tags?: string[];
      startedAt?: string;
      endedAt?: string;
    },
  ) {
    return request<{
      ok: boolean;
      sessionId: number;
      endNote: string;
      tag: string | null;
      tags?: string[];
      startedAt?: string;
      endedAt?: string;
      durationSeconds?: number;
    }>(
      `/api/sessions/${sessionId}/log`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async sessionsNotifyGitPushEta(payload: { projectId: number; minutes?: number }) {
    return request<{ ok: boolean; recipients: number; delivered: number; etaAt: string }>(
      '/api/sessions/git_push_eta',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async plansCreate(payload: {
    projectId: number;
    userId?: number;
    startTime: string;
    endTime: string;
    note?: string;
    createTask?: boolean;
  }) {
    return request<{ ok: boolean; id: number; taskId: number | null }>(
      '/api/plans/create',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async plansUpdate(
    id: number,
    payload: {
      projectId?: number;
      userId?: number;
      startTime?: string;
      endTime?: string;
      note?: string | null;
    },
  ) {
    return request<{ ok: boolean }>(
      `/api/plans/${id}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async plansList(
    filters: { from?: string; to?: string; projectId?: number; userId?: number } = {},
  ) {
    return request<{ items: PlanItem[] }>(`/api/plans/list${buildQuery(filters)}`);
  },

  async plansDelete(id: number) {
    return request<{ ok: boolean }>(`/api/plans/${id}`, { method: 'DELETE' }, { csrf: true });
  },

  async notesCreate(payload: {
    userId?: number;
    title: string;
    content?: string;
    color?: string;
    pinned?: boolean;
    visibility?: NoteVisibility;
    projectId?: number | null;
  }) {
    return request<{ ok: boolean; id: number }>(
      '/api/notes/create',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async notesList(
    filters: {
      userId?: number;
      includeArchived?: 0 | 1;
      scope?: 'mine' | 'team' | 'all';
      projectId?: number;
    } = {},
  ) {
    return request<{ items: NoteItem[] }>(`/api/notes/list${buildQuery(filters)}`);
  },

  async notesUpdate(
    id: number,
    payload: {
      title?: string;
      content?: string | null;
      color?: string;
      pinned?: boolean;
      archived?: boolean;
      visibility?: NoteVisibility;
      projectId?: number | null;
    },
  ) {
    return request<{ ok: boolean }>(
      `/api/notes/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async notesDelete(id: number) {
    return request<{ ok: boolean }>(`/api/notes/${id}`, { method: 'DELETE' }, { csrf: true });
  },

  // ---- Produkty: dashboardy cudzích appiek ----------------------------------

  async productsList(filters: { includeArchived?: 0 | 1 } = {}) {
    return request<{ items: ProductItem[] }>(`/api/products/list${buildQuery(filters)}`);
  },

  /** Pripojí samotnú ONVI WORKS ako produkt. Netreba adresu ani kľúč. */
  async productConnectSelf() {
    return request<{ ok: boolean; id: number; created: boolean }>(
      '/api/products/connect_self',
      { method: 'POST' },
      { csrf: true },
    );
  },
  async productCreate(payload: {
    name: string;
    color?: string;
    mode: 'pull' | 'push';
    provider?: 'supabase' | 'cloudflare_d1' | null;
    ref?: string | null;
    databaseId?: string | null;
    secret?: string | null;
    mapping?: ProductMapping;
    visibility?: ProductVisibility;
    viewerUserIds?: number[];
    visitsProvider?: VisitsProvider | null;
    visitsRef?: string | null;
    visitsSecret?: string | null;
    expectEveryMinutes?: number | null;
  }) {
    return request<{ ok: boolean; id: number; intakeSecret?: string }>(
      '/api/products/create',
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async productUpdate(
    id: number,
    payload: {
      name?: string;
      color?: string;
      provider?: 'supabase' | 'cloudflare_d1' | null;
      ref?: string | null;
      databaseId?: string | null;
      secret?: string | null;
      mapping?: ProductMapping;
      visibility?: ProductVisibility;
      viewerUserIds?: number[];
      archived?: boolean;
      visitsProvider?: VisitsProvider | null;
      visitsRef?: string | null;
      visitsSecret?: string | null;
      expectEveryMinutes?: number | null;
    },
  ) {
    return request<{ ok: boolean }>(
      `/api/products/${id}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async productDelete(id: number) {
    return request<{ ok: boolean }>(`/api/products/${id}`, { method: 'DELETE' }, { csrf: true });
  },

  /** Vydá nové tajomstvo pre push. To staré tým okamžite prestane platiť. */
  async productRotateSecret(id: number) {
    return request<{ ok: boolean; intakeSecret: string }>(
      `/api/products/${id}/rotate-secret`,
      { method: 'POST', body: JSON.stringify({}) },
      { csrf: true },
    );
  },

  async productDashboard(id: number, days = 30) {
    return request<ProductDashboard & { weekly?: ProductWeekly }>(
      `/api/products/${id}/dashboard${buildQuery({ days })}`,
    );
  },

  /** Cieľ metriky za 7 dní. `null` cieľ zruší. Smie iba ten, kto smie produkt upravovať. */
  async productTargetSet(id: number, metric: string, target: number | null) {
    return request<{ ok: boolean; targets: Record<string, number> }>(
      `/api/products/${id}/targets`,
      { method: 'PATCH', body: JSON.stringify({ metric, target }) },
      { csrf: true },
    );
  },

  /**
   * Chyby majú vlastné volanie, nie prívesok dashboardu.
   *
   * Filtruje a stránkuje server: keby sa úroveň vyberala až tu, strana by
   * vrátila päťdesiat riadkov, z ktorých by po odfiltrovaní ostali dva, a
   * kurzor by ukazoval inam, než človek vidí.
   */
  async productErrors(
    id: number,
    params: { days?: number; level?: ProductErrorLevel; cursor?: number } = {},
  ) {
    return request<{ errors: ProductError[]; nextCursor: number | null }>(
      `/api/products/${id}/errors${buildQuery(params)}`,
    );
  },

  /**
   * Čo je na produkte nastavené ako zdroj chýb.
   *
   * Bez tohto sa formulár otváral prázdny pri každej úprave: nedalo sa
   * zistiť, ktorá organizácia a ktorý projekt sú uložené, ani prečo zdroj
   * prestal zbierať. Token von nechodí, iba `hasSecret`.
   */
  async productSources(id: number) {
    return request<{ sources: ProductSourceItem[] }>(`/api/products/${id}/sources`);
  },

  /** Odpojí zdroj aj s uloženým kľúčom. Kľúč bez účelu je iba riziko. */
  async productSourceDelete(id: number, kind: ProductSourceItem['kind']) {
    return request<{ ok: boolean }>(
      `/api/products/${id}/sources/${kind}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  /**
   * Nastaví Sentry ako zdroj chýb. Token je nepovinný — keď nepríde, uložený
   * ostáva, takže zmena regiónu nezmaže kľúč.
   */
  async productSentrySource(
    id: number,
    payload: { region: SentryRegion; org: string; project: string; token?: string },
  ) {
    return request<{ ok: boolean }>(
      `/api/products/${id}/sources/sentry`,
      { method: 'PUT', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  /** Pull one collection now. Returns what it managed to read, or why not. */
  async productRefresh(id: number) {
    return request<{ ok: boolean; points: number; people: number; errors: number; warnings: string[] }>(
      `/api/products/${id}/refresh`,
      { method: 'POST', body: JSON.stringify({}) },
      { csrf: true },
    );
  },

  /** The SQL to paste into Supabase, and the snippet for the push path. */
  async productSetup(id: number) {
    return request<{ sql: string; snippet: string; intakeUrl: string }>(
      `/api/products/${id}/setup`,
    );
  },

  async productErrorToTask(id: number, errorId: number, projectId?: number | null) {
    return request<{ ok: boolean; taskId: number }>(
      `/api/products/${id}/errors/${errorId}/task`,
      { method: 'POST', body: JSON.stringify({ projectId: projectId ?? null }) },
      { csrf: true },
    );
  },

  /** Zmazať smie ten, kto výdavok zadal alebo zaplatil, a správcovia. */
  async expensesDelete(id: number) {
    return request<{ ok: boolean }>(`/api/expenses/${id}`, { method: 'DELETE' }, { csrf: true });
  },

  async expensesCreate(payload: {
    projectId?: number | null;
    title: string;
    amountCents: number;
    currency?: string;
    paidByUserId?: number;
    splitMode?: 'equal' | 'custom';
    participantUserIds: number[];
    customShares?: Array<{ userId: number; shareCents: number }>;
    expenseDate?: string;
    note?: string;
    category?: ExpenseCategory | null;
  }) {
    return request<{ ok: boolean; id: number }>(
      '/api/expenses/create',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async expensesList(
    filters: {
      projectId?: number;
      userId?: number;
      from?: string;
      to?: string;
      includeSettled?: 0 | 1;
    } = {},
  ) {
    return request<{ items: ExpenseItem[] }>(`/api/expenses/list${buildQuery(filters)}`);
  },

  async expensesSummary(
    filters: {
      projectId?: number;
      userId?: number;
      from?: string;
      to?: string;
      includeSettled?: 0 | 1;
    } = {},
  ) {
    return request<{
      items: Array<{
        userId: number;
        userName: string;
        paidCents: number;
        owedCents: number;
        balanceCents: number;
      }>;
      transfers: Array<{
        fromUserId: number;
        fromUserName: string;
        toUserId: number;
        toUserName: string;
        amountCents: number;
      }>;
    }>(`/api/expenses/summary${buildQuery(filters)}`);
  },

  async expensesUpdate(
    id: number,
    payload: {
      settled?: boolean;
      note?: string | null;
      category?: ExpenseCategory | null;
    },
  ) {
    return request<{ ok: boolean }>(
      `/api/expenses/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  // ---- Ideas ---------------------------------------------------------------
  async ideasList(filters: { projectId?: number; status?: string; sort?: string } = {}) {
    return request<{ items: Idea[] }>(`/api/ideas${buildQuery(filters)}`);
  },

  async ideaCreate(payload: { title: string; body?: string; projectId?: number | null }) {
    return request<{ ok: boolean; idea: Idea }>(
      '/api/ideas',
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async ideaGet(id: number) {
    return request<{ idea: Idea }>(`/api/ideas/${id}`);
  },

  async ideaUpdate(
    id: number,
    payload: {
      title?: string;
      body?: string | null;
      projectId?: number | null;
      status?: 'open' | 'rejected';
    },
  ) {
    return request<{ ok: boolean; idea: Idea }>(
      `/api/ideas/${id}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async ideaVote(id: number, value: -1 | 0 | 1) {
    return request<{ ok: boolean; idea: Idea }>(
      `/api/ideas/${id}/vote`,
      { method: 'POST', body: JSON.stringify({ value }) },
      { csrf: true },
    );
  },

  async ideaPrompt(id: number) {
    return request<{ ok: boolean; idea: Idea }>(
      `/api/ideas/${id}/prompt`,
      { method: 'POST' },
      { csrf: true },
    );
  },

  async ideaConvert(id: number, payload: { projectId?: number | null; priority?: string } = {}) {
    return request<{ ok: boolean; taskId: number; ticketNumber: number; idea: Idea }>(
      `/api/ideas/${id}/convert`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async ideaCommentsList(id: number) {
    return request<{ items: IdeaComment[] }>(`/api/ideas/${id}/comments`);
  },

  async ideaCommentUpload(id: number, file: Blob, name: string) {
    return request<{
      ok: boolean;
      attachment: { key: string; name: string; mime: string; size: number };
    }>(
      `/api/ideas/${id}/comment_upload?name=${encodeURIComponent(name)}`,
      {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      },
      { csrf: true },
    );
  },

  async ideaCommentCreate(
    id: number,
    payload: {
      body?: string;
      attachment?: { key: string; name: string; mime: string; size: number };
    },
  ) {
    return request<{ ok: boolean; id: number }>(
      `/api/ideas/${id}/comments`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async ideaCommentDelete(id: number, commentId: number) {
    return request<{ ok: boolean }>(
      `/api/ideas/${id}/comments/${commentId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  ideaCommentAttachmentUrl(id: number, commentId: number) {
    return `${API_BASE}/api/ideas/${id}/comments/${commentId}/attachment`;
  },

  async tasksCreate(payload: {
    projectId?: number | null;
    estimateMinutes?: number | null;
    userId?: number;
    testScenarioId?: number;
    title: string;
    note?: string;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    dueAt?: string | null;
    status?: 'open' | 'in_progress' | 'testing' | 'done';
    labelIds?: number[];
  }) {
    return request<{ ok: boolean; id: number; ticketNumber: number | null }>(
      '/api/tasks/create',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async tasksList(
    filters: {
      projectId?: number;
      userId?: number;
      includeCompleted?: 0 | 1;
      includeArchived?: 0 | 1;
    } = {},
  ) {
    return request<{ items: TodoTaskItem[] }>(`/api/tasks/list${buildQuery(filters)}`);
  },

  async tasksUpdate(
    id: number,
    payload: {
      projectId?: number | null;
      estimateMinutes?: number | null;
      testScenarioId?: number | null;
      userId?: number;
      title?: string;
      note?: string | null;
      priority?: 'low' | 'normal' | 'high' | 'urgent';
      dueAt?: string | null;
      completed?: boolean;
      archived?: boolean;
      status?: 'open' | 'in_progress' | 'testing' | 'done';
      labelIds?: number[];
    },
  ) {
    return request<{ ok: boolean }>(
      `/api/tasks/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  // `keepalive` pre zmazanie odoslané pri zatváraní karty: bez neho prehliadač
  // požiadavku pri odchode zo stránky zruší a tiketa, ktorú človek zmazal,
  // by sa potichu vrátila.
  async tasksDelete(id: number, options: { keepalive?: boolean } = {}) {
    return request<{ ok: boolean }>(
      `/api/tasks/${id}`,
      { method: 'DELETE', keepalive: options.keepalive },
      { csrf: true },
    );
  },

  // Copies what describes the work and none of what recorded it: the new ticket
  // starts open, with an unticked copy of the checklist.
  async tasksDuplicate(id: number) {
    return request<{ ok: boolean; id: number; ticketNumber: number | null }>(
      `/api/tasks/${id}/duplicate`,
      { method: 'POST' },
      { csrf: true },
    );
  },

  async tasksHistory(id: number) {
    return request<{ items: TaskHistoryItem[] }>(`/api/tasks/${id}/history`);
  },

  // ---- Bulk board actions --------------------------------------------------
  async tasksBulk(
    projectId: number,
    payload: {
      ids: number[];
      action:
        | 'status'
        | 'assign'
        | 'archive'
        | 'unarchive'
        | 'delete'
        | 'sprint'
        | 'label_add'
        | 'label_remove';
      status?: TaskStatus;
      userId?: number;
      sprintId?: number | null;
      labelId?: number;
    },
  ) {
    // affected = koľko ticketov sa naozaj zmenilo, matched = koľko ich prišlo.
    return request<{ ok: boolean; affected: number; matched?: number }>(
      `/api/projects/${projectId}/tasks/bulk`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  // ---- Recurring tickets ---------------------------------------------------
  async recurrencesList(projectId: number) {
    return request<{ items: TaskRecurrence[] }>(`/api/projects/${projectId}/recurrences`);
  },
  async recurrenceCreate(
    projectId: number,
    payload: {
      title: string;
      note?: string | null;
      priority?: 'low' | 'normal' | 'high' | 'urgent';
      everyDays: number;
      userId?: number;
      nextRunAt?: string;
    },
  ) {
    return request<{ ok: boolean; id: number | null }>(
      `/api/projects/${projectId}/recurrences`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async recurrenceUpdate(
    id: number,
    payload: {
      title?: string;
      note?: string | null;
      priority?: 'low' | 'normal' | 'high' | 'urgent';
      everyDays?: number;
      userId?: number;
      nextRunAt?: string;
      active?: boolean;
    },
  ) {
    return request<{ ok: boolean }>(
      `/api/recurrences/${id}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async recurrenceDelete(id: number) {
    return request<{ ok: boolean }>(`/api/recurrences/${id}`, { method: 'DELETE' }, { csrf: true });
  },

  // ---- WIP limits ----------------------------------------------------------
  // Only columns with a configured cap have a row; an unconfigured board is
  // unlimited as before.
  async wipLimitsList(projectId: number) {
    return request<{ items: WipLimit[] }>(`/api/projects/${projectId}/wip-limits`);
  },
  async wipLimitSet(projectId: number, payload: { status: TaskStatus; maxCount: number | null }) {
    return request<{ ok: boolean }>(
      `/api/projects/${projectId}/wip-limits`,
      { method: 'PUT', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  // ---- Sprints -------------------------------------------------------------
  async sprintsList(projectId: number) {
    return request<{ items: Sprint[] }>(`/api/projects/${projectId}/sprints`);
  },
  async sprintCreate(
    projectId: number,
    payload: { name: string; startsAt: string; endsAt: string },
  ) {
    return request<{ ok: boolean; id: number | null }>(
      `/api/projects/${projectId}/sprints`,
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async sprintUpdate(
    id: number,
    payload: { name?: string; startsAt?: string; endsAt?: string; closed?: boolean },
  ) {
    return request<{ ok: boolean }>(
      `/api/sprints/${id}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },
  async sprintDelete(id: number) {
    return request<{ ok: boolean }>(`/api/sprints/${id}`, { method: 'DELETE' }, { csrf: true });
  },
  async sprintBurndown(id: number) {
    return request<SprintBurndown>(`/api/sprints/${id}/burndown`);
  },

  async statsSummary(
    filters: { from?: string; to?: string; userId?: number; projectId?: number } = {},
  ) {
    return request<{
      totalDuration: number;
      sessionsCount: number;
      averageSessionLength: number;
      histogram: Array<{ day: string; totalDuration: number; sessionsCount: number }>;
    }>(`/api/stats/summary${buildQuery(filters)}`);
  },

  async statsByUser(
    filters: { from?: string; to?: string; userId?: number; projectId?: number } = {},
  ) {
    return request<{
      items: Array<{
        userId: number;
        userName: string;
        totalDuration: number;
        sessionsCount: number;
        averageSessionLength: number;
      }>;
    }>(`/api/stats/by_user${buildQuery(filters)}`);
  },

  async statsByProject(
    filters: { from?: string; to?: string; userId?: number; projectId?: number } = {},
  ) {
    return request<{
      items: Array<{
        projectId: number;
        projectName: string;
        color: string;
        totalDuration: number;
        sessionsCount: number;
        averageSessionLength: number;
      }>;
    }>(`/api/stats/by_project${buildQuery(filters)}`);
  },

  async pushGetPublicKey() {
    return request<{ publicKey: string }>('/api/push/vapid_public_key');
  },

  async pushStatus() {
    return request<{
      subscriptionCount: number;
      items: Array<{
        id: number;
        endpoint: string;
        createdAt: string;
        lastUsedAt: string | null;
        lastAttemptAt: string | null;
        lastStatusCode: number | null;
        lastError: string | null;
      }>;
    }>('/api/push/status');
  },

  async pushPreferences() {
    return request<{
      preferences: PushPreferences;
      quietHours: QuietHours;
      // Absolute instant, or null when Do Not Disturb is off or has expired.
      dndUntil: string | null;
    }>('/api/push/preferences');
  },

  async pushSetDnd(minutes: 30 | 60 | 120 | 480 | null) {
    return request<{ ok: boolean; dndUntil: string | null }>(
      '/api/push/dnd',
      { method: 'PATCH', body: JSON.stringify({ minutes }) },
      { csrf: true },
    );
  },

  async statusPresetsList() {
    return request<{ items: StatusPreset[] }>('/api/users/me/status-presets');
  },

  async statusPresetCreate(payload: { label: string; mode: StatusMode; color?: string | null }) {
    return request<{ ok: boolean; id: number }>(
      '/api/users/me/status-presets',
      { method: 'POST', body: JSON.stringify(payload) },
      { csrf: true },
    );
  },

  async statusPresetDelete(id: number) {
    return request<{ ok: boolean }>(
      `/api/users/me/status-presets/${id}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },

  // The per-type switches say what may be sent; this says when.
  async pushUpdateQuietHours(patch: Partial<QuietHours>) {
    return request<{ ok: boolean; quietHours: QuietHours }>(
      '/api/push/quiet-hours',
      { method: 'PATCH', body: JSON.stringify(patch) },
      { csrf: true },
    );
  },

  async pushUpdatePreferences(patch: Partial<PushPreferences>) {
    return request<{ ok: boolean; preferences: PushPreferences }>(
      '/api/push/preferences',
      {
        method: 'PATCH',
        body: JSON.stringify(patch),
      },
      { csrf: true },
    );
  },

  async pushSubscribe(subscription: PushSubscriptionJSON) {
    return request<{ ok: boolean }>(
      '/api/push/subscribe',
      {
        method: 'POST',
        body: JSON.stringify(subscription),
      },
      { csrf: true },
    );
  },

  async pushUnsubscribe(endpoint: string) {
    return request<{ ok: boolean }>(
      '/api/push/unsubscribe',
      {
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      },
      { csrf: true },
    );
  },

  async pushUnsubscribeAll() {
    return request<{ ok: boolean; deleted: number }>(
      '/api/push/unsubscribe_all',
      {
        method: 'POST',
      },
      { csrf: true },
    );
  },

  async pushTest(payload: { title: string; body: string; projectId?: number }) {
    return request<{
      ok: boolean;
      delivered: number;
      subscriptions: number;
      blocked: 'dnd' | 'quiet-hours' | 'preference' | null;
    }>(
      '/api/push/test',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      { csrf: true },
    );
  },

  async eventsAutoPause(reason: string) {
    return request<{ ok: boolean }>(
      '/api/events/auto_pause',
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
      { csrf: true },
    );
  },
  // --- Leady (obchodný zoznam projektu) ---------------------------------------
  async leadsList(projectId: number, params: Record<string, string | number | undefined> = {}) {
    return request<LeadListResponse>(`/api/projects/${projectId}/leads${buildQuery(params)}`);
  },
  async leadsFacets(projectId: number) {
    return request<LeadFacets>(`/api/projects/${projectId}/leads/facets`);
  },
  leadsExportUrl(projectId: number, params: Record<string, string | number | undefined> = {}) {
    return apiUrl(`/api/projects/${projectId}/leads/export.csv${buildQuery(params)}`);
  },
  async leadDetail(projectId: number, leadId: number) {
    return request<{ lead: LeadDetail }>(`/api/projects/${projectId}/leads/${leadId}`);
  },
  async leadCreate(projectId: number, body: LeadCreateInput) {
    return request<{ lead: LeadDetail }>(
      `/api/projects/${projectId}/leads`,
      { method: 'POST', body: JSON.stringify(body) },
      { csrf: true },
    );
  },
  async leadUpdate(projectId: number, leadId: number, body: LeadUpdateInput) {
    return request<{ lead: LeadDetail }>(
      `/api/projects/${projectId}/leads/${leadId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
      { csrf: true },
    );
  },
  async leadArchive(projectId: number, leadId: number, archived: boolean) {
    return request<{ ok: boolean }>(
      `/api/projects/${projectId}/leads/${leadId}/archive`,
      { method: 'POST', body: JSON.stringify({ archived }) },
      { csrf: true },
    );
  },
  async leadActivity(projectId: number, leadId: number, body: LeadActivityInput) {
    return request<{ lead: LeadDetail; statusChanged: boolean }>(
      `/api/projects/${projectId}/leads/${leadId}/activities`,
      { method: 'POST', body: JSON.stringify(body) },
      { csrf: true },
    );
  },
  async leadsImport(projectId: number, text: string, kind: 'json' | 'csv', dryRun: boolean) {
    return requestRaw<LeadImportResult>(
      `/api/projects/${projectId}/leads/import${dryRun ? '?dryRun=1' : ''}`,
      { method: 'POST', body: text, contentType: kind === 'csv' ? 'text/csv; charset=utf-8' : 'application/json' },
    );
  },
  async leadTemplates(projectId: number) {
    return request<{ items: LeadTemplate[] }>(`/api/projects/${projectId}/lead-templates`);
  },
  async leadTemplateSave(projectId: number, body: LeadTemplateInput, templateId?: number) {
    return request<{ id?: number; items: LeadTemplate[] }>(
      `/api/projects/${projectId}/lead-templates${templateId ? `/${templateId}` : ''}`,
      { method: templateId ? 'PATCH' : 'POST', body: JSON.stringify(body) },
      { csrf: true },
    );
  },
  async leadTemplateDelete(projectId: number, templateId: number) {
    return request<{ items: LeadTemplate[] }>(
      `/api/projects/${projectId}/lead-templates/${templateId}`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },
  async leadTemplateUpload(projectId: number, templateId: number, file: File) {
    return requestRaw<{ items: LeadTemplate[] }>(
      `/api/projects/${projectId}/lead-templates/${templateId}/attachment?name=${encodeURIComponent(file.name)}`,
      { method: 'POST', body: file, contentType: 'application/pdf' },
    );
  },
  async leadTemplateRemoveAttachment(projectId: number, templateId: number) {
    return request<{ items: LeadTemplate[] }>(
      `/api/projects/${projectId}/lead-templates/${templateId}/attachment`,
      { method: 'DELETE' },
      { csrf: true },
    );
  },
};
